import HeaderLayout from '~/components/ui/HeaderLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/components/ui/Card';
import { Avatar, AvatarImage } from '~/components/ui/Avatar';
import { amazon } from '~/assets/connectors/connectorLogo';
import { Separator } from '~/components/ui/Separator';
import { useToast } from '~/hooks/use-toast';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { apiClient } from '~/lib/apiClient';
import { type SetupGuideData } from '~/components/connector/ConnectorSetupGuide';
import { useAuthStore } from '~/stores/useAuthStore';
import { useActiveTeam } from '~/stores/useTeamStore';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '~/components/ui/Breadcrumb';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/Table';
import { Button } from '~/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/Dialog';
import ConnectorDocumentation from '~/components/connector/ConnectorDocumentation';
import { AmazonFileUploadForm } from '~/components/amazon/AmazonFileUploadForm';
import { InventoryTab } from '~/components/connector/InventoryFileUploadForm';

interface PastUpload {
  id: number;
  file_name: 'string';
  file_type: 'string';
  uploaded_by: 'string';
  upload_date: 'string';
}

const amazonSetupGuide: SetupGuideData = {
  prerequisites: [
    'Access to download CSV files from Amazon dashboard',
    'Properly formatted CSV files for campaign data upload',
    'Understanding of required data format for campaign data',
  ],
  steps: [
    {
      title: 'Prepare CSV File',
      description:
        'Format your campaign data according to the required specifications',
      note: 'Ensure all required columns are present and data is properly formatted',
    },
    {
      title: 'Upload File',
      description:
        'Use the file upload form to submit your CSV file to the Amazon system',
      note: 'File will be validated and processed automatically upon upload',
    },
    {
      title: 'Monitor Upload Status',
      description:
        'Check the upload history to track the status of your file submissions',
      note: 'You can revert uploads if needed using the Past Upload History section',
    },
  ],
};

export default function Amazon() {
  const { toast } = useToast();
  const refreshTeams = useAuthStore((state) => state.refreshTeams);
  const activeTeam = useActiveTeam();
  const [activeTab, setActiveTab] = useState<'campaigns' | 'inventory'>(
    'campaigns',
  );
  const [history, setHistory] = useState<PastUpload[]>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function fetchHistory() {
    try {
      const data = await apiClient.get<{
        upload_details?: PastUpload[];
      }>('/connectors/file/amazon');
      if (data.upload_details) {
        setHistory(data.upload_details);
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load history.',
        variant: 'destructive',
        duration: 3000,
      });
      setHistory([]);
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (activeTeam?.id) {
      fetchHistory();
    }
  }, [activeTeam?.id]);

  const revertUpload = async (uploadId: number) => {
    try {
      setLoading(true);
      const data = await apiClient.delete<{
        message?: string;
        detail?: string;
      }>(`/connectors/file/amazon/imports/${uploadId}`);

      if (data.message) {
        setMessage(data.message || 'Upload reverted back successfully');
        fetchHistory();
        refreshTeams();
        toast({
          title: 'Success',
          description: data.message || 'Upload reverted back successfully',
        });
      } else {
        setError(data.detail || 'Failed to revert upload');
        toast({
          title: 'Error',
          description: data.detail || 'Failed to revert upload',
          variant: 'destructive',
        });
      }
    } catch (err) {
      setError('Failed to revert upload');
      toast({
        title: 'Error',
        description: 'Failed to revert upload',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (error) {
      toast({
        title: 'Failed',
        duration: 3000,
        variant: 'destructive',
        description: error,
      });
      setError(null);
    }
    if (message) {
      toast({
        title: 'Success',
        duration: 3000,
        variant: 'default',
        description: message,
      });
      setMessage(null);
    }
  }, [error, message, toast]);

  return (
    <>
      <title>Amazon | DViO One</title>
      <meta name="description" content="Amazon Connector DViO ONE!" />

      <HeaderLayout>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/team/connections">Connectors</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="flex items-center gap-2">
                <Avatar className="size-6 rounded-none">
                  <AvatarImage src={amazon} className="object-contain" />
                </Avatar>
                Amazon
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </HeaderLayout>

      <nav className="flex justify-evenly border-y-2 border-border">
        <button
          onClick={() => setActiveTab('campaigns')}
          className={`p-4 text-sm font-medium transition-colors duration-200 border-b-2 w-full ${
            activeTab === 'campaigns'
              ? 'text-primary border-primary'
              : 'text-muted-foreground border-transparent hover:text-foreground hover:border-muted'
          }`}
        >
          Campaigns
        </button>
        <button
          onClick={() => setActiveTab('inventory')}
          className={`p-4 text-sm font-medium transition-colors duration-200 border-b-2 w-full ${
            activeTab === 'inventory'
              ? 'text-primary border-primary'
              : 'text-muted-foreground border-transparent hover:text-foreground hover:border-muted'
          }`}
        >
          Inventory
        </button>
      </nav>

      {activeTab === 'campaigns' && (
        <div className="w-full lg:h-[calc(100vh-4rem)] mx-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:h-full">
            <div className="lg:col-span-7 flex flex-col gap-4 lg:h-full min-h-0">
              <Card className="flex flex-col overflow-hidden transition-shadow hover:shadow-lg">
                <CardHeader className="shrink-0 py-3">
                  <CardTitle className="text-lg font-semibold">
                    File Upload
                  </CardTitle>
                </CardHeader>
                <Separator />
                <CardContent className="flex-1 overflow-auto pt-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <p className="text-sm font-medium leading-none">
                        File Type: Campaign Data
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Upload CSV for Campaign Data
                      </p>
                    </div>
                    <AmazonFileUploadForm
                      type="campaign-data"
                      onUploadSuccess={() => {
                        fetchHistory();
                        refreshTeams();
                      }}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="flex flex-col overflow-hidden flex-1 min-h-0 transition-shadow hover:shadow-lg">
                <CardHeader className="shrink-0 py-3">
                  <CardTitle className="text-lg font-semibold">
                    Past Upload History
                  </CardTitle>
                </CardHeader>
                <Separator />
                <div className="flex-1  overflow-hidden min-h-0">
                  <div className="rounded-md w-full max-w-full h-full mx-auto overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="min-w-[150px] font-semibold">
                            Filename
                          </TableHead>
                          <TableHead className="w-[140px] font-semibold">
                            File Type
                          </TableHead>
                          <TableHead className="w-[120px] font-semibold">
                            Uploaded By
                          </TableHead>
                          <TableHead className="w-[120px] font-semibold">
                            Upload Date
                          </TableHead>
                          <TableHead className="w-[120px] font-semibold text-right">
                            Action
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {history?.map((historyItem: PastUpload) => (
                          <TableRow
                            key={historyItem.id}
                            className="hover:bg-muted/30"
                          >
                            <TableCell
                              className="max-w-[200px] truncate"
                              title={String(historyItem.file_name)}
                            >
                              {historyItem.file_name}
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                                {historyItem.file_type}
                              </span>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {historyItem.uploaded_by}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {historyItem.upload_date}
                            </TableCell>
                            <TableCell className="text-right">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8"
                                    aria-label="Revert Upload"
                                  >
                                    Revert
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>
                                      Revert File Upload
                                    </DialogTitle>
                                    <DialogDescription>
                                      Are you sure you want to revert this
                                      upload. This action cannot be undone.
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="inline-flex justify-end">
                                    <Button
                                      variant="destructive"
                                      onClick={() =>
                                        revertUpload(historyItem.id)
                                      }
                                      disabled={loading}
                                      aria-label="Confirm Revert"
                                    >
                                      {loading ? 'Reverting...' : 'Confirm'}
                                    </Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </TableCell>
                          </TableRow>
                        ))}
                        {!history?.length && (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              className="text-muted-foreground text-center py-12"
                            >
                              <div className="flex flex-col items-center gap-2">
                                <span className="text-sm">
                                  No upload history found
                                </span>
                                <span className="text-xs">
                                  Upload a file to see it here
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </Card>
            </div>

            <div className="lg:col-span-5 flex flex-col">
              <Card className="flex-1 flex flex-col overflow-hidden transition-shadow hover:shadow-lg">
                <CardHeader className="shrink-0 pb-3">
                  <CardTitle className="text-lg font-semibold">
                    Documentation
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Setup guide
                  </CardDescription>
                </CardHeader>
                <Separator />
                <CardContent className="flex-1 overflow-auto pt-4">
                  <ConnectorDocumentation
                    setupGuide={amazonSetupGuide}
                    catalogueDescription="Explore Amazon data to be synced"
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'inventory' && (
        <InventoryTab connectorType="amazon" connectorLabel="Amazon" />
      )}
    </>
  );
}
